import type { ReactiveController, ReactiveControllerHost } from 'lit';

import { EDITABLE_SELECTOR } from '../dom/editable.js';

/**
 * Elements that hold focus in their own right. A press on one of these — or inside one —
 * must not have its focus taken away.
 *
 * `[tabindex]` covers anything the app made focusable deliberately, including a future
 * overlay; `[tabindex="-1"]` is included on purpose, since it is programmatically focusable
 * and something may well have focused it.
 */
const FOCUSABLE_SELECTOR = [
    // `:not(:disabled)` is load-bearing: a disabled control cannot hold or receive focus, so
    // a press on one must release the focus like a press on the canvas does. Without it a
    // disabled button in a future overlay would keep the stale focus and bring the original
    // defect back for that spot.
    'input:not(:disabled)', 'textarea:not(:disabled)', 'select:not(:disabled)',
    'button:not(:disabled)', 'a[href]',
    'audio[controls]', 'video[controls]', 'details', 'iframe',
    // `:not(:disabled)` here too, or it undoes the same care taken above: a disabled control
    // routinely still carries a tabindex, `[tabindex]` alone matches it first, and the stale
    // focus survives on an element that cannot hold focus at all. `:disabled` only ever matches
    // form controls, so a `<div tabindex="0">` is unaffected.
    '[tabindex]:not(:disabled)',
    // Every editable form, not just contenteditable="true" — see `EDITABLE_SELECTOR`.
    EDITABLE_SELECTOR,
].join(', ');

/**
 * Releases stale DOM focus when the user presses on the board surface.
 *
 * WHY THIS EXISTS
 *
 * A Konva canvas cannot take keyboard focus — it has no `tabindex`. Normally that is
 * harmless, because pressing a non-focusable element makes the browser move focus to the
 * document body. Something on the press path cancels the default action (so a drag does not
 * select text or scroll the page), and that cancellation takes the focus change with it.
 *
 * That "something" is THIS package, not Konva — worth stating precisely, because an earlier
 * version of this comment blamed Konva and sent readers to the wrong file. Konva's own
 * `_pointerdown` cancels only for touch (`Stage.js`: `if (shape.preventDefault() &&
 * evt.cancelable && isTouch)`), so a mouse press passes through it untouched. The cancellation
 * that produces this defect is `interaction.controller.ts` (`onStageDown`, three call sites:
 * the select tool on empty canvas and the two placement paths), which is bound to
 * `mousedown.interaction touchstart.interaction`. Its early return for `e.target !== stage`
 * is what decides whether a given press is cancelled at all.
 *
 * The result, measured in Chromium: after clicking a toolbar button and then clicking or
 * dragging on the board, the BUTTON still holds focus. Every later keystroke is aimed at it.
 * Releasing Space after panning was the reported case — the browser treats a Space release as
 * "activate the focused control", so panels opened and closed that nobody had touched.
 *
 * Suppressing that one keystroke would treat the symptom. This restores what the browser
 * would have done on its own: press the board, and whatever held focus lets go.
 *
 * WHY IT TRIGGERS ON THE PRESS, NOT ON HOVER OR ON A DRAG
 *
 * Hover would steal focus from a keyboard user whose pointer merely rests over the canvas,
 * and it would interrupt an open editor. A drag would be unambiguous but arrives later than
 * the press and misses a click that never moves — which is exactly the gesture the original
 * report describes ("hold Space, left-click, release Space").
 *
 * BLAST RADIUS — bounded to the board's own subtree by default
 *
 * `document.activeElement` is document-wide, so releasing it unconditionally would also blur
 * an element on the host page that has nothing to do with the whiteboard — a search field, a
 * chat input beside the board. That is what an earlier revision of this controller did, on the
 * argument that the stuck control sits OUTSIDE the board and a narrower rule could not reach
 * it. That argument was simply wrong, and measuring the wrapper is what settled it: `render()`
 * emits `<o2d-whiteboard-toolbar>`, `.board-root` and `<o2d-svg-optionsbar>` as Light-DOM
 * CHILDREN of the host, and all three editors are appended inside `.board-root`. Every control
 * the reported defect can strand is therefore inside the host element — AS THE WRAPPER RENDERS
 * IT.
 *
 * That qualifier is load-bearing. `<o2d-whiteboard-toolbar>` still carries a `board` property
 * (`@property({attribute: false}) board?: O2dWhiteboardElement`), the seam from the older
 * three-independently-placeable-elements design that the root CLAUDE.md still describes. A
 * toolbar placed outside the board and wired through that property is NOT a descendant of the
 * host, so the ownership check below skips it and this controller is a silent no-op for
 * precisely the control the report named. Measured: nothing places it that way today, and the
 * toolbar is not exported from the package's public entry point — so this is a latent
 * inconsistency, not a live gap. If that seam is ever used, this controller needs to consider
 * the wired toolbar part of the board.
 *
 * So the narrow rule costs nothing here and is the honest default for a package other pages
 * install: the board releases focus it owns, and reaches no further.
 *
 * There is deliberately NO flag to widen it back. A revision of this file carried one, added
 * while the permissive behaviour was still the default and the flag was its escape hatch. Once
 * the default became the narrow one, the flag had no caller left — only its own tests — and a
 * reflected property on a published element is a lasting commitment for a case nobody has
 * reported. Adding it later against a real consumer is backwards-compatible; carrying it now on
 * a guess is not free.
 *
 * WHY THERE IS NO SHADOW-ROOT DESCENT ON THE BLUR SIDE
 *
 * `document.activeElement` retargets: when the focused node lives inside an open shadow root,
 * it returns the shadow HOST, not the node. A revision of this file concluded that blurring the
 * retargeted host would therefore leave the inner node focused, and added a loop descending
 * `shadowRoot.activeElement` to reach it.
 *
 * That premise is false, measured in Chromium: focus an input inside an open shadow root, call
 * `blur()` on the HOST, and `shadowRoot.activeElement` becomes null. The host is the focus
 * delegate from outside, and blurring it releases the node. The descent was ten lines of code
 * and a paragraph defending a browser behaviour that does not exist.
 *
 * It looked necessary because the only test that observed it ran in happy-dom, which simulates
 * retargeting but not that part of the blur semantics. A guard that passes for a behaviour the
 * real browser does not have is worse than no guard: it makes speculative code look load-bearing.
 *
 * The TARGET side is different and the shadow handling there stays — see `keepsItsOwnFocus`.
 * That one IS observable in a real browser, and is guarded there.
 *
 * WHY IT CHECKS THE TARGET
 *
 * THREE editors are appended INSIDE `.board-root`, on top of the canvas, and a press that
 * lands in one of them must keep its focus or typing would break:
 *
 *   - textbox — `.textbox-editor`, a TipTap `contentEditable` (`textbox-editor.controller.ts`)
 *   - sticky  — `.sticky-editor`, a plain `contentEditable` div (`StickyEditorController`)
 *   - plain   — `.plain-editor`, `contentEditable="plaintext-only"` (`PlainEditController`),
 *               used by the checkbox and person features
 *
 * A revision of this comment reduced that to two, on the grounds that `src/features/` holds
 * only sticky and textbox. That was a wrong search rather than a fact: the third editor lives
 * in `src/core/edit/`, because it is shared by features rather than owned by one. The selector
 * covers it — `contenteditable="plaintext-only"` is matched by the
 * `:not([contenteditable="false"])` form, which is exactly why that form was chosen — so
 * behaviour was never affected, only the reasoning a reader would rely on. The check asks whether the TARGET can hold focus itself, rather than
 * whether it is the canvas — see `keepsItsOwnFocus` for why that direction is the one that
 * survives the next overlay.
 */
export class BoardFocusController implements ReactiveController {
  private readonly host: HTMLElement;
  private readonly container: HTMLElement;

  constructor(
    host: ReactiveControllerHost & HTMLElement,
    container: HTMLElement,
  ) {
    this.host = host;
    this.container = container;
    host.addController(this);
  }

  hostConnected(): void {
    // Capture phase, and NOT because of preventDefault — that only suppresses the browser's
    // own default action and leaves other listeners running. The reason is stopPropagation:
    // a handler on an inner node can end the bubble phase before it ever reaches this
    // container, and then a bubble-phase listener here would simply never fire. Capture runs
    // on the way down, before any of that.
    //
    // `pointerdown` is the primary listener because it covers mouse, touch AND pen with one
    // event. `touchstart` alone would miss a pen: the compatibility mouse events a pointer
    // normally also produces are suppressed once something calls preventDefault() on the
    // pointerdown — which is exactly what Konva does, and exactly the cause of this whole
    // defect. `mousedown` stays as a fallback for an environment without PointerEvent;
    // releasing focus twice is harmless.
    this.container.addEventListener('pointerdown', this.onPress, true);
    this.container.addEventListener('mousedown', this.onPress, true);
  }

  hostDisconnected(): void {
    this.container.removeEventListener('pointerdown', this.onPress, true);
    this.container.removeEventListener('mousedown', this.onPress, true);
  }

  private onPress = (e: Event): void => {
    // EVERY button, not just the primary one — decided, not overlooked. A right-click on the
    // board opens its context menu, which is as much an interaction with the board as a
    // left-click; leaving focus on a toolbar button there would keep the original defect
    // alive for that path (press Space afterwards and the button fires). The cost is that a
    // right-click on the board also blurs an input elsewhere on the page — which is correct:
    // the user has moved to the board.
    if (this.keepsItsOwnFocus(e)) return;

    // One view of the focus is enough. For a node inside an open shadow root this is the shadow
    // HOST — which is both the element that lives in the board's own tree (so the ownership test
    // below is asked about the right thing) and the element whose blur() releases the inner node
    // (measured; see the header).
    const active = document.activeElement;
    // SVG as well as HTML: an icon button can BE an svg element with its own tabindex, and
    // leaving that one focused would be the same stuck-focus defect in a different element
    // namespace. Both inherit blur() from HTMLOrSVGElement.
    if (!(active instanceof HTMLElement) && !(active instanceof SVGElement)) return;

    // No separate `active === document.body` check. It looked like a cheap guard and was in
    // fact unreachable: whenever body holds the focus, the retargeted node IS body, and a host
    // element is a descendant of body rather than the reverse, so the containment test below
    // already returns early. Measured — deleting it failed no test, which is the honest signal
    // that it never ran. A line that cannot run is not a safety net, it is a claim that
    // something was handled.
    //
    // Bounded to the board's own subtree.
    //
    // `contains` does not cross a shadow boundary, which is exactly why the RETARGETED node is
    // the right thing to ask about: retargeting already projects the focus onto the nearest
    // element in the light tree, so a focused input inside a shadow component sitting INSIDE
    // the board is correctly seen as the board's own.
    if (!this.host.contains(active)) return;

    active.blur();
  };

  /**
   * True when the press landed on something that can hold focus in its own right.
   *
   * Asked in this direction ON PURPOSE. The obvious alternative — "is this the drawing
   * surface?" — is a positive list (canvas, container), and everything NOT on it would keep
   * the stale focus. The board gains overlays over time, and the next unfocusable one (a
   * resize handle, a comment pin, a minimap) would reproduce exactly the defect this
   * controller exists to fix: neither canvas nor container, yet just as unable to take focus.
   * The list would need extending for each, and nobody would connect the symptom back to it.
   *
   * Turned around, the rule needs no maintenance: a press on something focusable or editable
   * leaves focus alone, everything else releases it. That is also the honest statement of the
   * intent — this is not about recognising the canvas, it is about not taking focus away from
   * something that wants it.
   *
   * The ancestor walk matters because a press usually lands on an inner node: a span inside
   * the TipTap editor, the label inside a button.
   *
   * WHY THE EVENT AND NOT `event.target`
   *
   * `event.target` retargets at a shadow boundary exactly as `document.activeElement` does: a
   * press inside an open shadow root reports the shadow HOST. Asking the coarser question here
   * would mean a focusable element inside a shadow component whose host carries no tabindex
   * goes unrecognised, and pressing it takes its focus away — the same defect this controller
   * exists to fix, one level down.
   *
   * Unlike the blur side (see the header), this one is NOT redundant in a real browser: the
   * host really does hide the inner target from `event.target`, and there is no delegation that
   * papers over it. Guarded in Chromium, not only in the simulated DOM.
   *
   * `composedPath()` is the event's own answer: the flattened path from the true innermost
   * target upward, shadow boundaries included. Walking it also makes the container bound a
   * property of the loop rather than a separate containment test — `contains()` would not have
   * crossed those boundaries either.
   */
  private keepsItsOwnFocus(event: Event): boolean {
    // No fallback to `event.target`. composedPath() returns an empty array only AFTER dispatch
    // has finished; inside a listener it is never empty, so the branch was unreachable in any
    // compliant browser. Worse, where it did run — a hand-built event in a test — it silently
    // replaced the ancestor walk with a single node, so a press on a span inside a button would
    // release that button's focus. A fallback that changes behaviour instead of preserving it
    // is not a safety net.
    for (const node of event.composedPath()) {
      // Bounded at the container ON PURPOSE, and the loop stops rather than tests: everything
      // before the container in the path is inside it by construction. An unbounded walk would
      // let a single `tabindex` on ANY ancestor — the host element, a focus-management wrapper
      // in the consuming page — answer true for every press and turn the whole controller into
      // a silent no-op. Measured in the demo the chain is clean (.board-root < o2d-whiteboard <
      // .board-wrap < body, no tabindex), but the consumer embeds the board in a different
      // page, and correctness should not depend on a property this code neither sees nor
      // enforces.
      //
      // The container itself does not count either: it IS the board surface, so a tabindex
      // placed on it would not mean "something here wants the focus". Breaking BEFORE the test
      // covers both.
      if (node === this.container) break;

      // Element, not HTMLElement: icon buttons are routinely `<button><svg><path/></svg></button>`,
      // and a press lands on the path. Narrowing to HTMLElement would skip the SVG nodes on the
      // way up and the enclosing button would go unrecognised — its focus released although a
      // control was pressed, the opposite of what this method is for. matches() is defined on
      // Element, so an SVG node answers just as well.
      //
      // NOTE: deliberately NOT `node.isContentEditable`. That property is INHERITED — an
      // element counts as editable when any ancestor is, however far up. A host page that
      // embeds the board inside a contenteditable region (a page editor, say) would make every
      // node under .board-root report true, and the controller would go silently dead. The
      // selector covers every editable form of the attribute, and this walk is bounded.
      if (node instanceof Element && node.matches(FOCUSABLE_SELECTOR)) return true;
    }

    return false;
  }
}
